import { message } from 'ant-design-vue';
import { closeAllNotifications } from '@/api/errorHandler';

/**
 * 顯示錯誤訊息（會先關閉所有 notification）
 * @param {string} content - 錯誤訊息內容
 * @param {number} duration - 顯示時長（秒），預設 3
 */
export const showErrorMessage = (content, duration = 3) => {
    closeAllNotifications();
    message.error(content, duration);
};

/**
 * 顯示成功訊息
 * @param {string} content - 成功訊息內容
 * @param {number} duration - 顯示時長（秒），預設 3
 */
export const showSuccessMessage = (content, duration = 3) => {
    message.success(content, duration);
};

/**
 * 顯示警告訊息
 * @param {string} content - 警告訊息內容
 * @param {number} duration - 顯示時長（秒），預設 3
 */
export const showWarningMessage = (content, duration = 3) => {
    message.warning(content, duration);
};

/**
 * 顯示資訊訊息
 * @param {string} content - 資訊訊息內容
 * @param {number} duration - 顯示時長（秒），預設 3
 */
export const showInfoMessage = (content, duration = 3) => {
    message.info(content, duration);
};

/**
 * 處理 API 回傳的驗證錯誤物件
 * 格式：{ result: false, message: "Validation failed", errors: { field: ["error message"] } }
 * @param {Object} response - API 回應物件
 * @param {Object} fieldLabels - 欄位名稱對應表（可選），例如 { name: "角色名稱" }
 * @returns {string} - 格式化後的錯誤訊息
 */
export const handleValidationErrors = (response, fieldLabels = {}) => {
    if (!response || !response.errors || typeof response.errors !== 'object') {
        return response?.message || '操作失敗';
    }

    const errorMessages = [];
    const errors = response.errors;

    // 遍歷所有錯誤欄位
    Object.keys(errors).forEach((field) => {
        const fieldErrors = errors[field];

        // 確保是陣列格式
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            // 取得欄位標籤（如果有提供對應表，否則使用原始欄位名稱）
            const fieldLabel = fieldLabels[field] || field;

            // 將所有錯誤訊息加入陣列
            fieldErrors.forEach((err) => {
                errorMessages.push(`${fieldLabel}：${err}`);
            });
        }
    });

    // 如果有錯誤訊息，返回組合後的訊息，否則返回預設訊息
    if (errorMessages.length > 0) {
        return errorMessages.join('\n');
    }

    return response?.message || '操作失敗';
};

/**
 * 統一處理 catch 區塊中的 API 錯誤
 * 會自動檢查是否有驗證錯誤（422），並使用 handleValidationErrors 處理
 * @param {Error} error - 錯誤物件
 * @param {string} defaultMessage - 預設錯誤訊息（可選）
 * @param {Object} fieldLabels - 欄位名稱對應表（可選），例如 { name: "角色名稱", id_number: "身份證號碼" }
 * @returns {void} - 直接顯示錯誤訊息，不返回值
 */
export const handleApiError = (
    error,
    defaultMessage = '操作失敗',
    fieldLabels = {}
) => {
    // 如果是表單驗證錯誤（Ant Design 的驗證），直接返回
    if (error.errorFields) {
        return;
    }

    // 嘗試從 error.response.data 或 error.data 取得錯誤資料
    const errorData = error?.response?.data || error?.data || error;

    // 檢查是否有驗證錯誤（422 或其他包含 errors 物件的錯誤）
    if (errorData?.errors && typeof errorData.errors === 'object') {
        // 使用 handleValidationErrors 處理驗證錯誤
        const errorMessage = handleValidationErrors(errorData, fieldLabels);
        showErrorMessage(errorMessage);
        return;
    }

    // 如果是網路錯誤或超時，顯示友善的訊息
    if (!error.response) {
        if (
            error.code === 'ECONNABORTED' ||
            error.message?.includes('timeout')
        ) {
            showErrorMessage('請求超時，請稍後再試');
            return;
        }
        if (
            error.message?.includes('fetch') ||
            error.message?.includes('Network')
        ) {
            showErrorMessage('網路連線失敗，請檢查網路連線');
            return;
        }
    }

    // 獲取最終顯示訊息，並過濾掉技術性的錯誤訊息（如 "Request failed with status code 422"）
    let displayMessage = errorData?.message || error.message || defaultMessage;
    if (
        typeof displayMessage === 'string' &&
        displayMessage.includes('Request failed with status code')
    ) {
        displayMessage = defaultMessage;
    }

    // 如果有有效的錯誤訊息，則顯示
    if (displayMessage) {
        showErrorMessage(displayMessage);
        return;
    }

    showErrorMessage(defaultMessage);
};
